import { useId } from 'react'
import { Kicker, ModalDialog } from './modal-dialog.tsx'

/**
 * The timezone confirmation — docs/ux/PLATFORM-ADMIN.md §12 and §19.
 *
 * It names the old zone and the new one, says that day boundaries move — the calendar,
 * exports and the expiry sweep — and says the two things that stop this reading as data loss:
 * past records keep the instants they were recorded at, and a job running right now is
 * unaffected.
 *
 * **A danger confirm, Escape refused, modal** (P06): §19 says every confirmation is, and this
 * one used to be the exception — a non-modal `<dialog open>` with a primary button. What it
 * changes is what "today" means for a tenant, which is reversible only by changing it back
 * *after* the day has already been cut the wrong way.
 *
 * **A failed save stays here** (§19), with the sentence inside and the button usable again; Cancel
 * closes it and clears the sentence. (A `validation_failed` that names a field is the exception:
 * the remedy is in the field behind the dialog, so the screen closes the dialog for it.)
 */
export function TimeZoneDialog({
  from,
  to,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  readonly from: string
  readonly to: string
  readonly pending: boolean
  /** A failed confirm's sentence, shown inside the dialog (§19). */
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onConfirm: () => void
}) {
  const headingId = useId()

  return (
    <ModalDialog labelledBy={headingId}>
      <Kicker lead="Moves the business day" />
      <h2 id={headingId}>
        Move this tenant’s business day from <code>{from}</code> to <code>{to}</code>?
      </h2>
      <ul>
        <li>
          What counts as “today” for this tenant changes: its assignment calendar, its export ranges
          and the nightly expiry sweep are all computed in this zone.
        </li>
        <li>
          <strong>Nothing already recorded is rewritten.</strong> Every report and every assignment
          keeps the exact moment it happened; only the day it falls into can move.
        </li>
        <li>A job running right now is unaffected.</li>
      </ul>

      {error === undefined ? null : (
        <p className="notice notice-failure" role="alert">
          {error}
        </p>
      )}

      <div className="confirm-controls">
        <button type="button" className="button" onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button
          type="button"
          className="button button-danger"
          onClick={onConfirm}
          disabled={pending}
        >
          {pending ? 'Saving…' : 'Change the timezone'}
        </button>
      </div>
    </ModalDialog>
  )
}
